(() => {
  if (window.ppCartUpsellsReady) return;
  window.ppCartUpsellsReady = true;

  const route = path => (window.Shopify?.routes?.root || '/') + path;
  const track = (eventName, payload) => window.PuzzlePupAnalytics?.track(eventName, payload);
  const impressedOffers = new Set();
  const acceptedOffers = new Set();
  let previousOffers = null;
  let addingOffer = false;

  const offerPayload = card => ({
    offer_id: card?.dataset.offerId,
    qualifier: card?.dataset.qualifier,
    offered_product: card?.dataset.offeredProduct,
    offered_product_id: card?.dataset.offeredProductId,
    variant_id: card?.dataset.variant,
    regular_price: Number(card?.dataset.regularPrice || 0),
    effective_price: Number(card?.dataset.effectivePrice || 0),
    offer_status: card?.dataset.offerStatus,
    page_location: window.location.pathname
  });

  const impressionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const card = entry.target;
      const offerId = card.dataset.offerId;
      if (!impressedOffers.has(offerId)) {
        impressedOffers.add(offerId);
        track('pp_offer_impression', { ...offerPayload(card), eligible_cart: true });
      }
      impressionObserver.unobserve(card);
    });
  }, { threshold: .35 }) : null;

  function reconcileOffers() {
    const current = new Map();
    document.querySelectorAll('[data-pp-offer-card]').forEach(card => {
      const offerId = card.dataset.offerId;
      if (!offerId) return;
      current.set(offerId, offerPayload(card));
      if (!card.dataset.ppImpressionReady) {
        card.dataset.ppImpressionReady = 'true';
        if (impressionObserver) impressionObserver.observe(card);
        else if (!impressedOffers.has(offerId)) {
          impressedOffers.add(offerId);
          track('pp_offer_impression', { ...offerPayload(card), eligible_cart: true });
        }
      }
    });
    if (previousOffers) {
      previousOffers.forEach((payload, offerId) => {
        if (current.has(offerId) || acceptedOffers.has(offerId)) return;
        track('pp_offer_removed_after_qualifier_change', { ...payload, removal_reason: 'qualifier_change' });
      });
    }
    previousOffers = current;
  }

  function initCarousel(root) {
    if (!root) return;
    const cards = [...root.querySelectorAll('[data-pp-offer-card]')];
    const signature = cards.map(card => card.dataset.offerId || card.dataset.variant || '').join('|');
    if (root.ppCarouselSignature === signature && typeof root.ppCarouselRefresh === 'function') {
      root.ppCarouselRefresh();
      return;
    }
    if (typeof root.ppCarouselCleanup === 'function') root.ppCarouselCleanup();
    root.ppCarouselSignature = signature;
    const controls = root.querySelector('[data-pp-offer-controls]');
    if (cards.length < 2) {
      root.classList.remove('is-carousel-ready', 'has-multiple-offers');
      root.removeAttribute('data-pp-carousel-ready');
      cards.forEach(card => card.classList.add('is-active'));
      if (controls) { controls.hidden = true; controls.style.display = 'none'; }
      const singleCurrent = root.querySelector('[data-pp-offer-current]');
      const singleTotal = root.querySelector('[data-pp-offer-total]');
      if (singleCurrent) singleCurrent.textContent = cards.length ? '1' : '0';
      if (singleTotal) singleTotal.textContent = String(cards.length);
      root.querySelectorAll('[data-pp-offer-prev],[data-pp-offer-next]').forEach(button => { button.disabled = true; });
      return;
    }
    root.classList.add('has-multiple-offers');
    root.dataset.ppCarouselReady = 'true';
    const current = root.querySelector('[data-pp-offer-current]');
    const total = root.querySelector('[data-pp-offer-total]');
    const previous = root.querySelector('[data-pp-offer-prev]');
    const next = root.querySelector('[data-pp-offer-next]');
    let index = 0;
    let maximumIndex = 0;
    const cardsPerView = () => {
      if (root.closest('.cart-drawer, cart-drawer')) return 1;
      if (window.matchMedia('(max-width: 749px)').matches) return 1;
      if (window.matchMedia('(max-width: 989px)').matches) return 2;
      return 3;
    };
    const show = requestedIndex => {
      const visibleCount = cardsPerView();
      maximumIndex = Math.max(0, cards.length - visibleCount);
      index = Math.min(Math.max(requestedIndex, 0), maximumIndex);
      const carouselEnabled = maximumIndex > 0;
      root.classList.toggle('is-carousel-ready', carouselEnabled);
      if (controls) { controls.hidden = !carouselEnabled; controls.style.display = carouselEnabled ? '' : 'none'; }
      cards.forEach((card, cardIndex) => {
        const visible = !carouselEnabled || (cardIndex >= index && cardIndex < index + visibleCount);
        card.classList.toggle('is-active', visible);
      });
      const visibleStart = index + 1;
      const visibleEnd = Math.min(index + visibleCount, cards.length);
      if (current) current.textContent = visibleStart === visibleEnd ? String(visibleStart) : `${visibleStart} to ${visibleEnd}`;
      if (total) total.textContent = String(cards.length);
      if (previous) previous.disabled = !carouselEnabled;
      if (next) next.disabled = !carouselEnabled;
    };
    const showPrevious = () => show(index === 0 ? maximumIndex : index - 1);
    const showNext = () => show(index === maximumIndex ? 0 : index + 1);
    previous?.addEventListener('click', showPrevious);
    next?.addEventListener('click', showNext);
    let resizeObserver = null;
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(() => show(index));
      resizeObserver.observe(root);
    }
    root.ppCarouselRefresh = () => show(index);
    root.ppCarouselCleanup = () => {
      previous?.removeEventListener('click', showPrevious);
      next?.removeEventListener('click', showNext);
      resizeObserver?.disconnect();
      delete root.ppCarouselRefresh;
      delete root.ppCarouselCleanup;
    };
    show(0);
  }

  const initCarousels = () => {
    document.querySelectorAll('[data-pp-cart-upsells]').forEach(initCarousel);
    reconcileOffers();
  };

  let carouselInitQueued = false;
  const queueCarouselInit = () => {
    if (carouselInitQueued) return;
    carouselInitQueued = true;
    window.requestAnimationFrame(() => {
      carouselInitQueued = false;
      initCarousels();
    });
  };

  async function cart() {
    const response = await fetch(route('cart.js'), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('The cart could not be verified.');
    return response.json();
  }

  async function removeLine(key) {
    const response = await fetch(route('cart/change.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
    if (!response.ok) throw new Error('The unconfirmed offer could not be removed.');
    return response.json();
  }

  async function refresh() {
    if (typeof window.PuzzlePupStorefront?.refreshCartSurfaces === 'function') {
      await window.PuzzlePupStorefront.refreshCartSurfaces();
    }
    document.dispatchEvent(new CustomEvent('pp:cart-updated'));
  }

  document.addEventListener('click', async event => {
    const button = event.target.closest('[data-pp-upsell-add]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled || addingOffer) return;
    addingOffer = true;
    const card = button.closest('[data-pp-offer-card]');
    const feedback = card?.querySelector('[data-pp-offer-feedback]');
    const variantId = Number(button.dataset.variant || 0);
    const expectedPrice = Number(button.dataset.expectedPrice || 0);
    const expectedTotal = Number(button.dataset.expectedTotal || 0);
    const analytics = offerPayload(card);
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding and checking price...';
    if (feedback) feedback.textContent = '';
    try {
      if (typeof window.PuzzlePupOfferEngine?.addAndVerify !== 'function') throw new Error('Offer verification is still loading. Please try again.');
      const verified = await window.PuzzlePupOfferEngine.addAndVerify({
        items: [{ id: variantId, quantity: Number(button.dataset.quantity || 1), expectedLineTotal: expectedPrice, properties: { '_PuzzlePup offer': 'Conditional cart add-on' } }],
        expectedCartTotal: expectedTotal,
        source: 'cart_upsell',
        offerId: analytics.offer_id,
        successEvent: 'pp_offer_verified'
      });
      const line = verified.lines[0];
      const refreshed = verified.cart;
      const confirmedPrice = Number(verified.cart.total_price || 0) - Number(verified.before.total_price || 0);
      acceptedOffers.add(analytics.offer_id);
      track('pp_offer_accept', {
        ...analytics,
        effective_price: confirmedPrice,
        discount_allocation_confirmed: true,
        resulting_cart_total: Number(refreshed.total_price || 0),
        incremental_order_value: confirmedPrice
      });
      const confirmed = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: verified.cart.currency || 'USD' }).format(verified.cart.total_price / 100);
      if (feedback) feedback.textContent = `${window.PuzzlePupCroLabels?.added || 'Added'} · ${confirmed}`;
      button.textContent = `${window.PuzzlePupCroLabels?.added || 'Added'} · ${confirmed}`;
      await window.PuzzlePupOfferEngine.refreshCartSurfaces();
      window.PuzzlePupStorefront?.openCartDrawer?.();
    } catch (error) {
      track('pp_offer_reject', { ...analytics, rejection_reason: error.message });
      if (feedback) feedback.textContent = error.message;
      const notice = document.querySelector('[data-pp-upsell-notice]');
      if (notice) notice.textContent = error.message;
      button.disabled = false;
      button.textContent = original;
    } finally {
      addingOffer = false;
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCarousels, { once: true });
  else initCarousels();
  document.addEventListener('pp:cart-updated', queueCarouselInit);
  document.addEventListener('cart:updated', queueCarouselInit);
  document.addEventListener('cart:refresh', queueCarouselInit);
  document.addEventListener('shopify:section:load', queueCarouselInit);
  new MutationObserver(mutations => {
    const hasNewCartOffers = mutations.some(mutation => [...mutation.addedNodes].some(node => {
      if (!(node instanceof Element)) return false;
      return node.matches('[data-pp-cart-upsells], cart-drawer') || Boolean(node.querySelector('[data-pp-cart-upsells]'));
    }));
    if (hasNewCartOffers) queueCarouselInit();
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
